import {
    type IBaseMFHRenovationProjectPosition,
    type ICreateMFHRenovationProjectCostBreakdownParams,
    type ICreateMFHRenovationProjectLotParams,
    type ICreateMFHRenovationProjectParams,
    type ICreateMFHRenovationProjectPlanningModuleParams,
    type ICreateMFHRenovationProjectPositions,
    type IMFHRenovationProject,
    type IMFHRenovationProjectCostBreakdown,
    type IMFHRenovationProjectLot,
    type IMFHRenovationProjectPlanningModule,
    type IMFHRenovationProjectPosition,
    type IUpdateMFHRenovationProject,
    type IUpdateMFHRenovationProjectCostBreakdownParams,
    type IUpdateMFHRenovationProjectLotParams,
    type IUpdateMFHRenovationProjectPlanningModule,
    type IUpdateMFHRenovationProjectPositionParams,
    type MFHRenovationProjectCostBreakdownEvent,
    type MFHRenovationProjectCostBreakdownExpandOpts,
    type MFHRenovationProjectExpandOpts,
    type MFHRenovationProjectPlanningModuleExpandOpts,
    type ReviewProcessEvents,
    type CreateTenderingProcessRequest,
    type ITenderingProcess,
    type TenderingProcessExpandOpts,
    type UpdateTenderingProcessRequest,
    type MFHRenovationProjectLotEvents,
    type MFHRenovationProjectPlanningModuleEvents,
    type MFHRenovationProjectEvents,
    type MFHRenovationProjectLotExpandOpts,
    type TenderingProcessTendererExpandOpts,
    type ITenderingProcessTenderer,
    type TenderingProcessTendererEvents,
    type IContract,
    type ContractExpandOpts,
    type CreateContractRequest,
    type UpdateContractRequest,
    type IAddendum,
    type AddendumExpandOpts,
    PositionCostAllocation,
    IPositionCostAllocation,
} from '@condo/domain';

import { getClient } from '../api-client';
import Decimal from 'decimal.js';

export const fetchMfhRenovationProjects = async (
    { mfhId, includeDeletedProjectsAndPlanningModules }: { mfhId: number; includeDeletedProjectsAndPlanningModules?: boolean },
    expandOpts: MFHRenovationProjectExpandOpts,
): Promise<IMFHRenovationProject[]> =>
    getClient('basecamp')
        .get<{ mfhRenovationProjects: IMFHRenovationProject[] }>(`/mfh/${mfhId}/renovation-projects`, {
            params: { expand: expandOpts, includeDeletedProjectsAndPlanningModules },
        })
        .then(response => response.data.mfhRenovationProjects);

export const fetchMfhRenovationProject = async (mfhRenovationProjectId: number, expandOpts: MFHRenovationProjectExpandOpts): Promise<IMFHRenovationProject> =>
    getClient('basecamp')
        .get<{ mfhRenovationProject: IMFHRenovationProject }>(`/mfh/renovation-projects/${mfhRenovationProjectId}`, { params: { expand: expandOpts } })
        .then(response => response.data.mfhRenovationProject);

export const deleteMfhRenovationProject = async (mfhRenovationProjectId: number): Promise<void> =>
    getClient('basecamp').delete(`/mfh/renovation-projects/${mfhRenovationProjectId}`);

export const restoreMfhRenovationProject = async (mfhRenovationProjectId: number): Promise<void> =>
    getClient('basecamp').put(`/mfh/renovation-projects/${mfhRenovationProjectId}/restore`);

export const createMfhRenovationProject = async (data: ICreateMFHRenovationProjectParams): Promise<IMFHRenovationProject> =>
    getClient('basecamp')
        .post<{ mfhRenovationProject: IMFHRenovationProject }>('/mfh/renovation-projects', data)
        .then(response => response.data.mfhRenovationProject);

export const updateMfhRenovationProject = async (mfhRenovationProjectId: number, data: IUpdateMFHRenovationProject['data']): Promise<void> =>
    getClient('basecamp').put(`/mfh/renovation-projects/${mfhRenovationProjectId}`, data);

export const assignMfhRenovationProjectBuildings = async (renovationProjectId: number, mfhBuildingIds: number[]): Promise<void> =>
    getClient('basecamp').put(`/mfh/renovation-projects/${renovationProjectId}/mfh-buildings`, { mfhBuildingIds });

export const fetchMfhRenovationProjectPlanningModule = async (
    mfhRenovationProjectPlanningModuleId: number,
    expandOpts: MFHRenovationProjectPlanningModuleExpandOpts,
): Promise<IMFHRenovationProjectPlanningModule> =>
    getClient('basecamp')
        .get<{ mfhRenovationProjectPlanningModule: IMFHRenovationProjectPlanningModule }>(
            `/mfh/renovation-projects/planning-modules/${mfhRenovationProjectPlanningModuleId}`,
            { params: { expand: expandOpts } },
        )
        .then(response => response.data.mfhRenovationProjectPlanningModule);

export const createMfhRenovationProjectPlanningModule = async (
    mfhRenovationProjectId: number,
    data: Omit<ICreateMFHRenovationProjectPlanningModuleParams, 'mfhRenovationProjectId'>,
): Promise<IMFHRenovationProjectPlanningModule> => getClient('basecamp').post(`/mfh/renovation-projects/${mfhRenovationProjectId}/planning-modules`, data);

export const deleteMfhRenovationProjectPlanningModule = async (mfhRenovationProjectPlanningModuleId: number): Promise<void> =>
    getClient('basecamp').delete(`/mfh/renovation-projects/planning-modules/${mfhRenovationProjectPlanningModuleId}`);

export const restoreMfhRenovationProjectPlanningModule = async (mfhRenovationProjectPlanningModuleId: number): Promise<void> =>
    getClient('basecamp').put(`/mfh/renovation-projects/planning-modules/${mfhRenovationProjectPlanningModuleId}/restore`);

export const updateMfhRenovationProjectPlanningModule = async (
    mfhRenovationProjectPlanningModuleId: number,
    data: IUpdateMFHRenovationProjectPlanningModule['data'],
): Promise<void> => getClient('basecamp').put(`/mfh/renovation-projects/planning-modules/${mfhRenovationProjectPlanningModuleId}`, data);

export const createMfhRenovationProjectLot = async (
    mfhRenovationProjectId: number,
    data: Omit<ICreateMFHRenovationProjectLotParams, 'mfhRenovationProjectId'>,
): Promise<IMFHRenovationProjectLot> => getClient('basecamp').post(`/mfh/renovation-projects/${mfhRenovationProjectId}/lots`, data);

export const updateMfhRenovationProjectLot = async (
    mfhRenovationProjectLotId: number,
    data: Omit<IUpdateMFHRenovationProjectLotParams, 'mfhRenovationProjectLotId'>,
): Promise<void> => getClient('basecamp').put(`/mfh/renovation-projects/lots/${mfhRenovationProjectLotId}`, data);

export const deleteMfhRenovationProjectLot = async (mfhRenovationProjectLotId: number): Promise<void> =>
    getClient('basecamp').delete(`/mfh/renovation-projects/lots/${mfhRenovationProjectLotId}`);

export const fetchMfhRenovationProjectLot = async (
    mfhRenovationProjectLotId: number,
    expandOpts: MFHRenovationProjectLotExpandOpts,
): Promise<IMFHRenovationProjectLot> =>
    getClient('basecamp')
        .get<{ mfhRenovationProjectLot: IMFHRenovationProjectLot }>(`/mfh/renovation-projects/lots/${mfhRenovationProjectLotId}`, {
            params: { expand: expandOpts },
        })
        .then(response => response.data.mfhRenovationProjectLot);

export const fetchMfhRenovationProjectCostBreakdown = async (
    mfhRenovationProjectCostBreakdownId: number,
    expandOpts: MFHRenovationProjectCostBreakdownExpandOpts,
): Promise<IMFHRenovationProjectCostBreakdown> =>
    getClient('basecamp')
        .get<{ mfhRenovationProjectCostBreakdown: IMFHRenovationProjectCostBreakdown }>(
            `/mfh/renovation-projects/cost-breakdowns/${mfhRenovationProjectCostBreakdownId}`,
            { params: { expand: expandOpts } },
        )
        .then(response => response.data.mfhRenovationProjectCostBreakdown);

export const createMfhRenovationProjectCostBreakdown = async (
    mfhRenovationProjectId: number,
    mfhRenovationProjectCostBreakdown: Omit<ICreateMFHRenovationProjectCostBreakdownParams, 'mfhRenovationProjectId'>,
): Promise<IMFHRenovationProjectCostBreakdown> =>
    getClient('basecamp')
        .post<{ mfhRenovationProjectCostBreakdown: IMFHRenovationProjectCostBreakdown }>(
            `/mfh/renovation-projects/${mfhRenovationProjectId}/cost-breakdowns`,
            mfhRenovationProjectCostBreakdown,
        )
        .then(response => response.data.mfhRenovationProjectCostBreakdown);

export const updateMfhRenovationProjectCostBreakdown = async (
    mfhRenovationProjectCostBreakdown: number,
    data: IUpdateMFHRenovationProjectCostBreakdownParams['data'],
): Promise<void> => getClient('basecamp').put(`/mfh/renovation-projects/cost-breakdowns/${mfhRenovationProjectCostBreakdown}`, data);

export const getMfhRenovationProjectPositionSheetUrl = async (mfhRenovationProjectCostBreakdownId?: number): Promise<string> =>
    getClient('basecamp')
        .get(`/mfh/renovation-projects/positions-import-sheet-url`, {
            params: {
                mfhRenovationProjectCostBreakdownId,
            },
        })
        .then(response => response.data.url);

export const parseMfhRenovationProjectPositionsFromSheet = async (spreadsheetUrl: string): Promise<Partial<IMFHRenovationProjectPosition>[]> =>
    getClient('basecamp')
        .post(`/mfh/renovation-projects/cost-breakdowns/positions/parse`, { spreadsheetUrl })
        .then(response => response.data.data);

export const createMfhRenovationProjectPositions = async (
    mfhRenovationProjectCostBreakdownId: number,
    mfhRenovationProjectPositions: ICreateMFHRenovationProjectPositions['data'],
    linkableDocumentIds?: number[],
): Promise<Partial<IBaseMFHRenovationProjectPosition>[]> =>
    getClient('basecamp')
        .post<{ mfhRenovationProjectPositions: IBaseMFHRenovationProjectPosition[] }>(
            `/mfh/renovation-projects/cost-breakdowns/${mfhRenovationProjectCostBreakdownId}/positions`,
            {
                mfhRenovationProjectPositions,
                linkableDocumentIds,
            },
        )
        .then(response => response.data.mfhRenovationProjectPositions);

export const createMfhRenovationProjectPosition = async (
    mfhRenovationProjectCostBreakdownId: number,
    mfhRenovationProjectPosition: IMFHRenovationProjectPosition,
): Promise<IBaseMFHRenovationProjectPosition> =>
    getClient('basecamp')
        .post<{ mfhRenovationProjectPosition: IBaseMFHRenovationProjectPosition }>(
            `/mfh/renovation-projects/cost-breakdowns/${mfhRenovationProjectCostBreakdownId}/position`,
            {
                mfhRenovationProjectPosition,
            },
        )
        .then(response => response.data.mfhRenovationProjectPosition);

export const updateMfhRenovationProjectPosition = async (
    mfhRenovationProjectPositionId: number,
    mfhRenovationProjectPosition: IUpdateMFHRenovationProjectPositionParams['mfhRenovationProjectPosition'],
): Promise<IBaseMFHRenovationProjectPosition> =>
    getClient('basecamp')
        .put<{ mfhRenovationProjectPosition: IBaseMFHRenovationProjectPosition }>(
            `/mfh/renovation-projects/cost-breakdowns/positions/${mfhRenovationProjectPositionId}`,
            {
                mfhRenovationProjectPosition,
            },
        )
        .then(response => response.data.mfhRenovationProjectPosition);

export const deleteMfhRenovationProjectPosition = async (mfhRenovationProjectPositionId: number): Promise<void> =>
    getClient('basecamp').delete(`/mfh/renovation-projects/cost-breakdowns/positions/${mfhRenovationProjectPositionId}`);

export const postProcessMFHRenovationProjectCostBreakdownEvent = async ({
    event,
    eventDate,
    mfhRenovationProjectCostBreakdownId,
    additionalData,
}: {
    mfhRenovationProjectCostBreakdownId: number;
    event: ReviewProcessEvents | MFHRenovationProjectCostBreakdownEvent;
    eventDate: Date;
    additionalData?: Record<string, any>;
}): Promise<{}> =>
    getClient('basecamp').post(`/mfh/renovation-projects/cost-breakdowns/${mfhRenovationProjectCostBreakdownId}/process-event`, {
        event,
        eventDate,
        additionalData,
    });

export const postMfhRenovationProjectLotEvent = async ({
    mfhRenovationProjectLotId,
    event,
    eventDate,
    additionalData,
}: {
    mfhRenovationProjectLotId: number;
    event: MFHRenovationProjectLotEvents;
    eventDate: Date;
    additionalData?: Record<string, any>;
}): Promise<{}> => getClient('basecamp').post(`/mfh/renovation-projects/lots/${mfhRenovationProjectLotId}/event`, { event, eventDate, additionalData });

export const postMfhRenovationProjectPlanningModuleEvent = async ({
    mfhRenovationProjectPlanningModuleId,
    event,
    eventDate,
    additionalData,
}: {
    mfhRenovationProjectPlanningModuleId: number;
    event: MFHRenovationProjectPlanningModuleEvents;
    eventDate: Date;
    additionalData?: Record<string, any>;
}): Promise<{}> =>
    getClient('basecamp').post(`/mfh/renovation-projects/planning-modules/${mfhRenovationProjectPlanningModuleId}/event`, { event, eventDate, additionalData });

export const postMfhRenovationProjectEvent = async ({
    mfhRenovationProjectId,
    event,
    eventDate,
    additionalData,
}: {
    mfhRenovationProjectId: number;
    event: MFHRenovationProjectEvents;
    eventDate: Date;
    additionalData?: Record<string, any>;
}): Promise<{}> => getClient('basecamp').post(`/mfh/renovation-projects/${mfhRenovationProjectId}/event`, { event, eventDate, additionalData });

export const fetchTenderingProcess = async (tenderingProcessId: number, expandOpts: TenderingProcessExpandOpts): Promise<ITenderingProcess> =>
    getClient('basecamp')
        .get<{ tenderingProcess: ITenderingProcess }>(`/mfh/renovation-projects/tendering-process/${tenderingProcessId}`, {
            params: { expand: expandOpts },
        })
        .then(response => response.data.tenderingProcess);

export const createTenderingProcess = async (
    mfhRenovationProjectId: number,
    tenderingProcess: CreateTenderingProcessRequest['tenderingProcess'],
): Promise<ITenderingProcess> =>
    getClient('basecamp')
        .post(`/mfh/renovation-projects/${mfhRenovationProjectId}/tendering-process`, { tenderingProcess })
        .then(response => response.data.tenderingProcess);

export const updateTenderingProcess = async (
    tenderingProcessId: number,
    tenderingProcess: UpdateTenderingProcessRequest['tenderingProcess'],
): Promise<ITenderingProcess> =>
    getClient('basecamp')
        .patch(`/mfh/renovation-projects/tendering-process/${tenderingProcessId}`, { tenderingProcess })
        .then(response => response.data.tenderingProcess);

export const deleteTenderingProcess = async (tenderingProcessId: number): Promise<void> =>
    getClient('basecamp').delete(`/mfh/renovation-projects/tendering-process/${tenderingProcessId}`);

export interface IImportTenderingProcessOffersResult {
    status: 'SUCCESS' | 'FAILED';
    positionsImported?: number;
    errors?: string[];
}

export const importTenderingProcessOffersFromExcel = async (
    tenderingProcessId: number,
    tenderingProcessTendererId: number,
    file: File,
): Promise<IImportTenderingProcessOffersResult> => {
    const formData = new FormData();
    formData.append('file', file);

    return getClient('basecamp')
        .post<IImportTenderingProcessOffersResult[]>(
            `/mfh/renovation-projects/tendering-process/${tenderingProcessId}/tenderers/${tenderingProcessTendererId}/import-offers`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            },
        )
        .then(response => response.data[0]);
};

export const updateTenderingProcessOffer = async (tenderingProcessOfferId: number, internalAssessment?: string, isFinal?: boolean): Promise<void> =>
    getClient('basecamp')
        .patch(`/mfh/renovation-projects/tendering-process-offers/${tenderingProcessOfferId}`, {
            internalAssessment,
            isFinal,
        })
        .then(response => response.data);

export const deleteTenderingProcessOffer = async (tenderingProcessOfferId: number): Promise<void> =>
    getClient('basecamp').delete(`/mfh/renovation-projects/tendering-process-offers/${tenderingProcessOfferId}`);

export const fetchTenderingProcessTenderer = async (
    tenderingProcessTendererId: number,
    expandOpts: TenderingProcessTendererExpandOpts,
): Promise<ITenderingProcessTenderer> =>
    getClient('basecamp')
        .get<{ tenderingProcessTenderer: ITenderingProcessTenderer }>(`/mfh/renovation-projects/tendering-process/tenderer/${tenderingProcessTendererId}`, {
            params: { expand: expandOpts },
        })
        .then(response => response.data.tenderingProcessTenderer);

export const updateTenderingProcessWinnerTenderer = async (tenderingProcessId: number, tenderingProcessTendererId: number, isWinner: boolean): Promise<void> =>
    getClient('basecamp').post(`/mfh/renovation-projects/tendering-process/${tenderingProcessId}/tenderer/${tenderingProcessTendererId}/update-winner`, {
        isWinner,
    });

export const postTenderingProcessTendererEvent = async ({
    tenderingProcessTendererId,
    event,
}: {
    tenderingProcessTendererId: number;
    event: TenderingProcessTendererEvents;
}): Promise<{}> => getClient('basecamp').post(`/mfh/renovation-projects/tendering-process/tenderer/${tenderingProcessTendererId}/process-event`, { event });

export const fetchContracts = async (mfhRenovationProjectId: number, expandOpts: ContractExpandOpts): Promise<IContract[]> =>
    getClient('basecamp')
        .get<{ contracts: IContract[] }>(`/mfh/renovation-projects/${mfhRenovationProjectId}/contracts`, {
            params: { expand: expandOpts },
        })
        .then(response => response.data.contracts);

export const fetchContract = async (contractId: number, expandOpts: ContractExpandOpts): Promise<IContract> =>
    getClient('basecamp')
        .get<{ contract: IContract }>(`/mfh/renovation-projects/contracts/${contractId}`, {
            params: { expand: expandOpts },
        })
        .then(response => response.data.contract);

export const createContract = async (mfhRenovationProjectId: number, contract: CreateContractRequest['contract']): Promise<IContract> =>
    getClient('basecamp')
        .post<{ contract: IContract }>(`/mfh/renovation-projects/${mfhRenovationProjectId}/contracts`, { contract })
        .then(response => response.data.contract);

export const updateContract = async (contractId: number, contract: UpdateContractRequest['contract']): Promise<IContract> =>
    getClient('basecamp')
        .put<{ contract: IContract }>(`/mfh/renovation-projects/contracts/${contractId}`, { contract })
        .then(response => response.data.contract);

export const uploadContractPdf = async (contractId: number, files: File[]): Promise<IContract> => {
    const formData = new FormData();
    files.forEach(f => formData.append('file', f));
    return getClient('basecamp')
        .post<{ contract: IContract }>(`/mfh/renovation-projects/contracts/${contractId}/pdf`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(response => response.data.contract);
};

export const deleteContract = async (contractId: number): Promise<void> => getClient('basecamp').delete(`/mfh/renovation-projects/contracts/${contractId}`);

export const fetchContractsSearch = async (params: {
    mfhId?: number;
    nameSearch?: string;
    page?: number;
    size?: number;
    expand: ContractExpandOpts;
}): Promise<{ contracts: IContract[]; total: number }> =>
    getClient('basecamp')
        .get<{ contracts: IContract[]; total: number }>('/mfh/contracts', { params })
        .then(response => response.data);

export const fetchAddenda = async (params: {
    mfhId?: number;
    nameSearch?: string;
    page?: number;
    size?: number;
    expand: AddendumExpandOpts;
}): Promise<{ addenda: Partial<IAddendum>[]; total?: number }> =>
    getClient('basecamp')
        .get<{ addenda: Partial<IAddendum>[]; total?: number }>('/addenda', { params })
        .then(response => response.data);

export const fetchCostBreakdownPositions = async (costBreakdownId: number): Promise<IMFHRenovationProjectPosition[]> =>
    getClient('basecamp')
        .get<IMFHRenovationProjectPosition[]>(`/mfh/renovation-projects/cost-breakdowns/${costBreakdownId}/positions`, {})
        .then(response => {
            const refined = response.data.map(p => ({
                ...p,
                ...(p.prognosis ? { prognosis: new Decimal(p.prognosis) } : {}),
                ...(p.externalCostAttributionPercentage ? { externalCostAttributionPercentage: new Decimal(p.externalCostAttributionPercentage) } : {}),
                ...(p.adjustedContractValue ? { adjustedContractValue: new Decimal(p.adjustedContractValue) } : {}),
                ...(p.costAmendmentTotal ? { costAmendmentTotal: new Decimal(p.costAmendmentTotal) } : {}),
                ...(p.costConcern ? { costConcern: new Decimal(p.costConcern) } : {}),
                ...(p.invoicesBilledTotal ? { invoicesBilledTotal: new Decimal(p.invoicesBilledTotal) } : {}),
                ...(p.uncommittedCost ? { uncommittedCost: new Decimal(p.uncommittedCost) } : {}),
                costAllocations: (p.costAllocations ?? []).map(ca => new PositionCostAllocation(ca as unknown as IPositionCostAllocation)),
            }));
            return refined;
        });
